import { NgModule } from '@angular/core';
import { Routes, RouterModule } from '@angular/router';
import { OverlayModule } from '@angular/cdk/overlay';
import { PortalModule } from '@angular/cdk/portal';
import { AccountingPreloadingStrategy } from './accounting-preloading.strategy';
import { resolutionTypeGuard } from './guards/resolution-type.guard';
import { accountingActivationGuard } from './guards/accounting-activation.guard';
import { authGuard } from './guards/auth.guard';
import { welcomeGuard } from './guards/welcome.guard';
import { teamInvitationGuard } from './guards/team-invitation.guard';
import { partnerGuard } from './guards/partner.guard';
import { altomateonlyGuard } from './guards/altomateonly.guard';
import { permissionGuard } from './guards/permission.guard';
import { environment } from '../environments/environment';
import { newGuestGuard } from './guards/new-guest.guard';

const routes: Routes = [
  { path: '', pathMatch: 'full', redirectTo: '/welcome' },
  {
    path: 'home',
    loadChildren: () => import('./pages/home/home.module').then(m => m.HomePageModule),
    canActivate: [altomateonlyGuard, authGuard],
  },
  {
    path: 'new-home',
    loadChildren: () => import('./pages/new-home/new-home.module').then(m => m.NewHomePageModule),
  },
  {
    // Developer-only gallery of the UI library (src/app/shared/ui). Not available in production builds.
    path: 'ui-gallery',
    canMatch: [() => !environment.production],
    loadChildren: () => import('./pages/ui-gallery/ui-gallery.routes').then(m => m.UI_GALLERY_ROUTES),
  },
  {
    path: 'not-found',
    loadChildren: () => import('./pages/not-found/not-found.module').then(m => m.NotFoundPageModule)
  },
  {
    path: 'register',
    loadChildren: () => import('./pages/register/register.module').then(m => m.RegisterPageModule),
    canActivate: [altomateonlyGuard, welcomeGuard]
  },
  {
    path: 'welcome',
    loadChildren: () => import('./pages/welcome/welcome.module').then(m => m.WelcomePageModule),
    canActivate: [altomateonlyGuard, welcomeGuard]
  },
  {
    path: 'cosec/:id',
    loadChildren: () => import('./pages/cosec/cosec.module').then(m => m.CosecPageModule),
    canActivate: [altomateonlyGuard, authGuard],
  },
  {
    path: 'cosec/:id/:uuid',
    loadChildren: () => import('./pages/cosec/cosec.module').then(m => m.CosecPageModule),
    canActivate: [partnerGuard],
  },
  {
    path: 'cosec-amendment/:id',
    loadChildren: () => import('./pages/cosec-amendment/cosec-amendment.module').then(m => m.CosecAmendmentPageModule),
    canActivate: [altomateonlyGuard, authGuard],
  },
  {
    path: 'forgot-password',
    loadChildren: () => import('./pages/forgot-password/forgot-password.module').then(m => m.ForgotPasswordPageModule),
    canActivate: [altomateonlyGuard],
  },
  {
    path: 'resolutions/:type',
    loadChildren: () => import('./pages/resolutions/resolutions.module').then(m => m.ResolutionsPageModule),
    canActivate: [altomateonlyGuard, resolutionTypeGuard],
  },
  {
    path: 'resolutions-form/:type/:uuid',
    loadChildren: () => import('./pages/resolutions/resolutions.module').then(m => m.ResolutionsPageModule),
    canActivate: [partnerGuard, resolutionTypeGuard],
  },
  {
    path: 'resolutions/:type/:id',
    loadChildren: () => import('./pages/resolutions/resolutions.module').then(m => m.ResolutionsPageModule),
    canActivate: [altomateonlyGuard, authGuard, permissionGuard, resolutionTypeGuard],
    data: { requiredPermissions: ['Company Secretary'] },
  },
  {
    path: 'resolution-list',
    loadChildren: () => import('./pages/resolution-list/resolution-list.module').then(m => m.ResolutionListPageModule),
    canActivate: [altomateonlyGuard, authGuard, permissionGuard],
    data: { requiredPermissions: ['Company Secretary'] },
  },
  {
    path: 'login',
    loadChildren: () => import('./pages/login/login.module').then(m => m.LoginPageModule),
    canActivate: [altomateonlyGuard, welcomeGuard]
  },
  {
    path: 'document',
    loadChildren: () => import('./pages/document/document.module').then(m => m.DocumentPageModule),
    canActivate: [altomateonlyGuard, authGuard, permissionGuard],
    data: { requiredPermissions: ['Document'] },
  },
  {
    path: 'kyc',
    loadChildren: () => import('./pages/kyc/kyc.module').then(m => m.KycPageModule),
    canActivate: [altomateonlyGuard, authGuard],
  },
  {
    path: 'kyc/:uuid',
    loadChildren: () => import('./pages/kyc/kyc.module').then(m => m.KycPageModule),
    canActivate: [partnerGuard],
  },
  {
    path: 'task/:task_id',
    loadChildren: () => import('./pages/task/task.module').then(m => m.TaskPageModule),
    canActivate: [altomateonlyGuard, authGuard],
  },
  {
    path: 'task/:task_id/:uuid',
    loadChildren: () => import('./pages/task/task.module').then(m => m.TaskPageModule),
    canActivate: [partnerGuard],
  },
  {
    path: 'task-list',
    loadChildren: () => import('./pages/task-list/task-list.module').then(m => m.TaskListPageModule),
    canActivate: [altomateonlyGuard, authGuard],
  },
  {
    path: 'task-list/:id',
    loadChildren: () => import('./pages/task-list/task-list.module').then(m => m.TaskListPageModule),
    canActivate: [altomateonlyGuard, authGuard],
  },
  {
    path: 'request/:request_id',
    loadChildren: () => import('./pages/request/request.module').then(m => m.RequestPageModule),
    canActivate: [altomateonlyGuard, authGuard],
  },
  {
    path: 'request-list',
    loadChildren: () => import('./pages/request-list/request-list.module').then(m => m.RequestListPageModule),
    canActivate: [altomateonlyGuard, authGuard],
  },
  {
    path: 'profile',
    loadChildren: () => import('./pages/profile/profile.module').then(m => m.ProfilePageModule),
    canActivate: [altomateonlyGuard, authGuard],
  },
  {
    path: 'reset-password',
    loadChildren: () => import('./pages/reset-password/reset-password.module').then(m => m.ResetPasswordPageModule),
    canActivate: [altomateonlyGuard, authGuard],
  },
  {
    path: 'billings',
    loadChildren: () => import('./pages/billings/billings.module').then(m => m.BillingsPageModule),
    canActivate: [altomateonlyGuard, authGuard, permissionGuard],
    data: { requiredPermissions: ['Plan & Billing'] },
  },
  {
    path: 'company-info',
    loadChildren: () => import('./pages/company-info/company-info.module').then(m => m.CompanyInfoPageModule),
    canActivate: [altomateonlyGuard, authGuard, permissionGuard],
    data: { requiredPermissions: ['Company Info'] },
  },
  {
    path: 'accounting',

    loadChildren: () => import('./pages/accounting/accounting.module').then(m => m.AccountingPageModule),
    // accountingActivationGuard sits on the parent because activate/plan is a
    // CHILD of accounting here (payroll mounts its wizard as a sibling). The
    // guard branches on the URL: it redirects an active company OUT of the
    // wizard, always allows /accounting/home, and requires an active
    // entitlement everywhere else. See guards/accounting-activation.guard.ts.
    canActivate: [altomateonlyGuard, authGuard, accountingActivationGuard],
  },
  {
    path: 'delegate-access',
    loadChildren: () => import('./pages/delegate-access/delegate-access.module').then(m => m.DelegateAccessPageModule),
    canActivate: [altomateonlyGuard, authGuard],
  },
  {
    path: 'webcam',
    loadChildren: () => import('./pages/webcam/webcam.module').then(m => m.WebcamPageModule),
    canActivate: [altomateonlyGuard],
  },
  {
    path: 'confirm',
    loadChildren: () => import('./modals/confirm/confirm.module').then(m => m.ConfirmPageModule),
    canActivate: [altomateonlyGuard],
  },
  {
    path: 'mobile-web',
    loadChildren: () => import('./modals/mobile-web/mobile-web.module').then(m => m.MobileWebPageModule),
    canActivate: [altomateonlyGuard],
  },
  {
    path: 'incorp-progress',
    loadChildren: () => import('./modals/incorp-progress/incorp-progress.module').then(m => m.IncorpProgressPageModule),
    canActivate: [altomateonlyGuard],
  },
  {
    path: 'businesses',
    loadChildren: () => import('./pages/businesses/businesses.module').then(m => m.BusinessesPageModule),
    canActivate: [altomateonlyGuard, authGuard],
  },
  {
    path: 'update',
    loadChildren: () => import('./modals/update/update.module').then(m => m.UpdatePageModule),
    canActivate: [altomateonlyGuard],
  },
  {
    path: 'products',
    loadChildren: () => import('./pages/products/products.module').then(m => m.ProductsPageModule),
    canActivate: [altomateonlyGuard],
  },
  {
    path: 'inappupdate',
    loadChildren: () => import('./modals/inappupdate/inappupdate.module').then(m => m.InappupdatePageModule),
    canActivate: [altomateonlyGuard],
  },
  {
    path: 'documents',
    loadChildren: () => import('./pages/documents/documents.module').then(m => m.DocumentsPageModule),
    canActivate: [altomateonlyGuard, authGuard, permissionGuard],
    data: { requiredPermissions: ['Document'] },
  },
  {
    path: 'new-documents',
    loadChildren: () => import('./pages/new-documents/new-documents.module').then(m => m.NewDocumentsPageModule),
    canActivate: [altomateonlyGuard, authGuard, permissionGuard],
    data: { requiredPermissions: ['Document'] },
  },
  {
    path: 'new-name-check',
    loadChildren: () => import('./pages/new-name-check/new-name-check.module').then(m => m.NewNameCheckPageModule),
    canActivate: [altomateonlyGuard]
  },
  {
    path: 'new-tax-calculator',
    loadChildren: () => import('./pages/new-tax-calculator/new-tax-calculator.module').then(m => m.NewTaxCalculatorPageModule),
    canActivate: [altomateonlyGuard]
  },
  {
    path: 'new-billing',
    loadChildren: () => import('./pages/new-billing/new-billing.module').then(m => m.NewBillingPageModule),
    canActivate: [altomateonlyGuard, authGuard, permissionGuard],
    data: { requiredPermissions: ['Plan & Billing'] },
  },
  {
    path: 'new-invoices',
    loadChildren: () => import('./pages/new-invoices/new-invoices.module').then(m => m.NewInvoicesPageModule),
    canActivate: [altomateonlyGuard, authGuard, permissionGuard],
    data: { requiredPermissions: ['Plan & Billing'] },
  },
  {
    path: 'new-subscriptions',
    loadChildren: () => import('./pages/new-subscriptions/new-subscriptions.module').then(m => m.NewSubscriptionsPageModule),
    canActivate: [altomateonlyGuard, authGuard, permissionGuard],
    data: { requiredPermissions: ['Plan & Billing'] },
  },
  {
    path: 'new-payment-methods',
    loadChildren: () => import('./pages/new-payment-methods/new-payment-methods.module').then(m => m.NewPaymentMethodsPageModule),
    canActivate: [altomateonlyGuard, authGuard, permissionGuard],
    data: { requiredPermissions: ['Plan & Billing'] },
  },
  {
    path: 'new-services',
    loadChildren: () => import('./pages/new-services/new-services.module').then(m => m.NewServicesPageModule),
    canActivate: [altomateonlyGuard]
  },
  {
    path: 'new-cosec',
    loadChildren: () => import('./pages/new-cosec/new-cosec.module').then(m => m.NewCosecPageModule),
    canActivate: [altomateonlyGuard]
  },
  {
    path: 'new-task-details',
    loadChildren: () => import('./pages/new-task-details/new-task-details.module').then(m => m.NewTaskDetailsPageModule),
    canActivate: [altomateonlyGuard]
  },
  {
    path: 'new-resolution-details',
    loadChildren: () => import('./pages/new-resolution-details/new-resolution-details.module').then(m => m.NewResolutionDetailsPageModule),
    canActivate: [altomateonlyGuard]
  },
  {
    path: 'new-welcome',
    loadChildren: () => import('./pages/new-welcome/new-welcome.module').then(m => m.NewWelcomePageModule),
    canActivate: [altomateonlyGuard]
  },
  {
    path: 'new-login',
    loadChildren: () => import('./pages/new-login/new-login.module').then(m => m.NewLoginPageModule),
    canActivate: [altomateonlyGuard, newGuestGuard]
  },
  {
    path: 'new-register',
    loadChildren: () => import('./pages/new-register/new-register.module').then(m => m.NewRegisterPageModule),
    canActivate: [altomateonlyGuard, newGuestGuard]
  },
  {
    path: 'new-forgot-password',
    loadChildren: () => import('./pages/new-forgot-password/new-forgot-password.module').then(m => m.NewForgotPasswordPageModule),
    canActivate: [altomateonlyGuard, newGuestGuard]
  },
  {
    path: 'new-reset-password/:token',
    loadChildren: () => import('./pages/new-reset-password/new-reset-password.module').then(m => m.NewResetPasswordPageModule),
    canActivate: [altomateonlyGuard]
  },
  {
    path: 'new-whatsapp-support',
    loadChildren: () => import('./pages/new-whatsapp-support/new-whatsapp-support.module').then(m => m.NewWhatsappSupportPageModule),
    canActivate: [altomateonlyGuard]
  },
  {
    path: 'new-help-center',
    loadChildren: () => import('./pages/new-help-center/new-help-center.module').then(m => m.NewHelpCenterPageModule),
    canActivate: [altomateonlyGuard]
  },
  {
    path: 'new-help-category',
    loadChildren: () => import('./pages/new-help-category/new-help-category.module').then(m => m.NewHelpCategoryPageModule),
    canActivate: [altomateonlyGuard]
  },
  {
    path: 'new-compliance',
    loadChildren: () => import('./pages/new-compliance/new-compliance.module').then(m => m.NewCompliancePageModule),
    canActivate: [altomateonlyGuard, authGuard, permissionGuard],
    data: { requiredPermissions: ['Company Secretary'] },
  },
  {
    path: 'new-settings',
    loadChildren: () => import('./pages/new-settings/new-settings.module').then(m => m.NewSettingsPageModule),
    canActivate: [altomateonlyGuard, authGuard],
  },
  {
    path: 'new-team',
    loadChildren: () => import('./pages/new-team/new-team.module').then(m => m.NewTeamPageModule),
    canActivate: [altomateonlyGuard, authGuard, permissionGuard],
    data: { requiredPermissions: ['Team'] },
  },
  {
    path: 'new-team-member',
    loadChildren: () => import('./pages/new-team-member/new-team-member.module').then(m => m.NewTeamMemberPageModule),
    canActivate: [altomateonlyGuard, authGuard, permissionGuard],
    data: { requiredPermissions: ['Team'] },
  },
  {
    path: 'new-team-access',
    loadChildren: () => import('./pages/new-team-access/new-team-access.module').then(m => m.NewTeamAccessPageModule),
    canActivate: [altomateonlyGuard, authGuard, permissionGuard],
    data: { requiredPermissions: ['Team'] },
  },
  {
    path: 'new-team-invite',
    loadChildren: () => import('./pages/new-team-invite/new-team-invite.module').then(m => m.NewTeamInvitePageModule),
    canActivate: [altomateonlyGuard, authGuard, permissionGuard],
    data: { requiredPermissions: ['Team'] },
  },
  {
    path: 'new-team-invitation',
    loadChildren: () => import('./pages/new-team-invitation/new-team-invitation.module').then(m => m.NewTeamInvitationPageModule),
    canActivate: [altomateonlyGuard, authGuard, permissionGuard],
    data: { requiredPermissions: ['Team'] },
  },
  { path: 'new-not-found', loadChildren: () => import('./pages/new-status/new-status.module').then(m => m.NewStatusPageModule), data: { kind: 'not-found' } },
  { path: 'new-no-access', loadChildren: () => import('./pages/new-status/new-status.module').then(m => m.NewStatusPageModule), data: { kind: 'no-access' } },
  { path: 'new-link-expired', loadChildren: () => import('./pages/new-status/new-status.module').then(m => m.NewStatusPageModule), data: { kind: 'link-expired' } },
  {
    path: 'new-company-info',
    loadChildren: () => import('./pages/new-company-info/new-company-info.module').then(m => m.NewCompanyInfoPageModule),
    canActivate: [altomateonlyGuard, permissionGuard],
    data: { requiredPermissions: ['Company Info'] },
  },
  {
    path: 'new-referrals',
    loadChildren: () => import('./pages/new-referrals/new-referrals.module').then(m => m.NewReferralsPageModule),
    canActivate: [altomateonlyGuard],
  },
  {
    path: 'new-notifications',
    loadChildren: () => import('./pages/new-notifications/new-notifications.module').then(m => m.NewNotificationsPageModule),
    canActivate: [altomateonlyGuard, authGuard],
  },
  {
    path: 'new-domains',
    loadChildren: () => import('./pages/new-domains/new-domains.module').then(m => m.NewDomainsPageModule),
    canActivate: [altomateonlyGuard, authGuard],
  },
  {
    path: 'new-domain-search',
    loadChildren: () => import('./pages/new-domain-search/new-domain-search.module').then(m => m.NewDomainSearchPageModule),
    canActivate: [altomateonlyGuard, authGuard],
  },
  {
    path: 'new-domain-checkout',
    loadChildren: () => import('./pages/new-domain-checkout/new-domain-checkout.module').then(m => m.NewDomainCheckoutPageModule),
    canActivate: [altomateonlyGuard, authGuard],
  },
  {
    path: 'new-domain-success',
    loadChildren: () => import('./pages/new-domain-success/new-domain-success.module').then(m => m.NewDomainSuccessPageModule),
    canActivate: [altomateonlyGuard, authGuard],
  },
  {
    path: 'new-domain-overview',
    loadChildren: () => import('./pages/new-domain-overview/new-domain-overview.module').then(m => m.NewDomainOverviewPageModule),
    canActivate: [altomateonlyGuard, authGuard],
  },
  {
    path: 'new-domain-dns',
    loadChildren: () => import('./pages/new-domain-dns/new-domain-dns.module').then(m => m.NewDomainDnsPageModule),
    canActivate: [altomateonlyGuard, authGuard],
  },
  {
    path: 'new-domain-forwarding',
    loadChildren: () => import('./pages/new-domain-forwarding/new-domain-forwarding.module').then(m => m.NewDomainForwardingPageModule),
    canActivate: [altomateonlyGuard, authGuard],
  },
  {
    path: 'new-domain-nameservers',
    loadChildren: () => import('./pages/new-domain-nameservers/new-domain-nameservers.module').then(m => m.NewDomainNameserversPageModule),
    canActivate: [altomateonlyGuard, authGuard],
  },
  {
    path: 'new-domain-renew',
    loadChildren: () => import('./pages/new-domain-renew/new-domain-renew.module').then(m => m.NewDomainRenewPageModule),
    canActivate: [altomateonlyGuard, authGuard],
  },
  {
    path: 'new-domain-settings',
    loadChildren: () => import('./pages/new-domain-settings/new-domain-settings.module').then(m => m.NewDomainSettingsPageModule),
    canActivate: [altomateonlyGuard, authGuard],
  },
  {
    path: 'new-emails',
    loadChildren: () => import('./pages/new-emails/new-emails.module').then(m => m.NewEmailsPageModule),
    canActivate: [altomateonlyGuard, authGuard],
  },
  {
    path: 'new-email-plans',
    loadChildren: () => import('./pages/new-email-plans/new-email-plans.module').then(m => m.NewEmailPlansPageModule),
    canActivate: [altomateonlyGuard, authGuard],
  },
  {
    path: 'new-email-checkout',
    loadChildren: () => import('./pages/new-email-checkout/new-email-checkout.module').then(m => m.NewEmailCheckoutPageModule),
    canActivate: [altomateonlyGuard, authGuard],
  },
  {
    path: 'new-email-domain',
    loadChildren: () => import('./pages/new-email-domain/new-email-domain.module').then(m => m.NewEmailDomainPageModule),
    canActivate: [altomateonlyGuard, authGuard],
  },
  {
    path: 'new-email-dns',
    loadChildren: () => import('./pages/new-email-dns/new-email-dns.module').then(m => m.NewEmailDnsPageModule),
    canActivate: [altomateonlyGuard, authGuard],
  },
  {
    path: 'new-email-dashboard',
    loadChildren: () => import('./pages/new-email-dashboard/new-email-dashboard.module').then(m => m.NewEmailDashboardPageModule),
    canActivate: [altomateonlyGuard, authGuard],
  },
  {
    path: 'new-email-accounts',
    loadChildren: () => import('./pages/new-email-accounts/new-email-accounts.module').then(m => m.NewEmailAccountsPageModule),
    canActivate: [altomateonlyGuard, authGuard],
  },
  {
    path: 'new-email-account-create',
    loadChildren: () => import('./pages/new-email-account-create/new-email-account-create.module').then(m => m.NewEmailAccountCreatePageModule),
    canActivate: [altomateonlyGuard, authGuard],
  },
  {
    path: 'new-email-account',
    loadChildren: () => import('./pages/new-email-account/new-email-account.module').then(m => m.NewEmailAccountPageModule),
    canActivate: [altomateonlyGuard, authGuard],
  },
  {
    path: 'new-email-forwarding',
    loadChildren: () => import('./pages/new-email-forwarding/new-email-forwarding.module').then(m => m.NewEmailForwardingPageModule),
    canActivate: [altomateonlyGuard, authGuard],
  },
  {
    path: 'new-email-forwarding-rule',
    loadChildren: () => import('./pages/new-email-forwarding-rule/new-email-forwarding-rule.module').then(m => m.NewEmailForwardingRulePageModule),
    canActivate: [altomateonlyGuard, authGuard],
  },
  {
    path: 'new-email-spam',
    loadChildren: () => import('./pages/new-email-spam/new-email-spam.module').then(m => m.NewEmailSpamPageModule),
    canActivate: [altomateonlyGuard, authGuard],
  },
  {
    path: 'new-logo-creation',
    loadChildren: () => import('./pages/new-logo-creation/new-logo-creation.module').then(m => m.NewLogoCreationPageModule),
    canActivate: [altomateonlyGuard, authGuard],
  },
  {
    path: 'new-business-card-creation',
    loadChildren: () => import('./pages/new-business-card-creation/new-business-card-creation.module').then(m => m.NewBusinessCardCreationPageModule),
    canActivate: [altomateonlyGuard, authGuard],
  },
  {
    path: 'new-trademark',
    loadChildren: () => import('./pages/new-trademark/new-trademark.module').then(m => m.NewTrademarkPageModule),
    canActivate: [altomateonlyGuard, authGuard],
  },
  {
    path: 'new-invitation',
    loadChildren: () => import('./pages/new-invitation/new-invitation.module').then(m => m.NewInvitationPageModule)
  },
  {
    path: 'rp/:uid',
    loadChildren: () => import('./pages/rp/rp.module').then(m => m.RpPageModule),
    canActivate: [altomateonlyGuard],
  },
  {
    path: 'start-login',
    loadChildren: () => import('./pages/login/login.module').then(m => m.LoginPageModule),
    canActivate: [altomateonlyGuard, welcomeGuard]
  },
  {
    path: 'start',
    loadChildren: () => import('./pages/welcome/welcome.module').then(m => m.WelcomePageModule),
    canActivate: [altomateonlyGuard, welcomeGuard]
  },
  {
    path: 'landing',
    loadChildren: () => import('./pages/welcome/welcome.module').then(m => m.WelcomePageModule),
    canActivate: [altomateonlyGuard, welcomeGuard]
  },
  {
    path: 'resolution-success',
    loadChildren: () => import('./modals/resolution-success/resolution-success.module').then(m => m.ResolutionSuccessPageModule),
    canActivate: [altomateonlyGuard]
  },
  {
    path: 'upload-signed-documents',
    loadChildren: () => import('./modals/upload-signed-documents/upload-signed-documents.module').then(m => m.UploadSignedDocumentsPageModule),
    canActivate: [altomateonlyGuard]
  },
  {
    path: 'confirm-input',
    loadChildren: () => import('./modals/confirm-input/confirm-input.module').then(m => m.ConfirmInputPageModule),
    canActivate: [altomateonlyGuard]
  },
  {
    path: 'shop-services',
    loadChildren: () => import('./pages/shop-services/shop-services.module').then(m => m.ShopServicesPageModule),
    canActivate: [altomateonlyGuard]
  },
  {
    path: 'test',
    loadChildren: () => import('./pages/test/test.module').then(m => m.TestPageModule),
    canActivate: [altomateonlyGuard]
  },
  {
    path: 'shop/order/:uuid',
    loadComponent: () => import('./pages/shop/shop-order/shop-order.page').then(m => m.ShopOrderPage),
    canActivate: [altomateonlyGuard]
  },
  {
    path: 'shop/:slug/checkout',
    loadComponent: () => import('./pages/shop/shop-checkout/shop-checkout.page').then(m => m.ShopCheckoutPage),
    canActivate: [altomateonlyGuard]
  },
  {
    path: 'shop/:slug',
    loadComponent: () => import('./pages/shop/shop-service/shop-service.page').then(m => m.ShopServicePage),
    canActivate: [altomateonlyGuard]
  },
  {
    path: 'shop-service-details',
    loadChildren: () => import('./pages/shop-service-details/shop-service-details.module').then(m => m.ShopServiceDetailsPageModule),
    canActivate: [altomateonlyGuard]
  },
  {
    path: 'tax-calculator',
    loadChildren: () => import('./pages/tax-calculator/tax-calculator.module').then(m => m.TaxCalculatorPageModule),
    canActivate: [altomateonlyGuard]
  },
  {
    path: 'customers',
    loadChildren: () => import('./pages/customers/customers.module').then(m => m.CustomersPageModule),
    canActivate: [altomateonlyGuard]
  },
  {
    path: 'cosec-partner/:id',
    loadChildren: () => import('./pages/cosec-partner/cosec-partner.module').then(m => m.CosecPartnerPageModule),
    canActivate: [altomateonlyGuard, authGuard],
  },
  {
    path: 'cosec-partner/:id/:uuid',
    loadChildren: () => import('./pages/cosec-partner/cosec-partner.module').then(m => m.CosecPartnerPageModule),
    canActivate: [partnerGuard],
  },
  {
    path: 'trademark',
    loadChildren: () => import('./pages/trademark/trademark.module').then(m => m.TrademarkPageModule)
  },
  {
    path: 'payroll',
    loadChildren: () => import('./pages/payroll/payroll.module').then(m => m.PayrollPageModule)
  },
  {
    path: 'vouchers',
    loadChildren: () => import('./pages/vouchers/vouchers.module').then(m => m.VouchersPageModule)
  },
  {
    path: 'domains',
    loadChildren: () => import('./pages/domains/domains.module').then(m => m.DomainsPageModule)
  },
  {
    path: 'dns-details',
    loadChildren: () => import('./modals/domains/dns-details/dns-details.module').then(m => m.DnsDetailsPageModule)
  },
  {
    path: 'accountings',
    loadChildren: () => import('./pages/accountings/accountings.module').then(m => m.AccountingsPageModule)
  },
  {
    path: 'payment-status/:transactionId',
    loadChildren: () => import('./pages/payment-status/payment-status.module').then(m => m.PaymentStatusPageModule)
  },
  {
    path: 'forwarding',
    loadChildren: () => import('./modals/domains/forwarding/forwarding.module').then(m => m.ForwardingPageModule)
  },
  {
    path: 'logo-creation',
    loadChildren: () => import('./pages/logo-creation/logo-creation.module').then(m => m.LogoCreationPageModule)
  },
  {
    path: 'business-card-creation',
    loadChildren: () => import('./pages/business-card-creation/business-card-creation.module').then(m => m.BusinessCardCreationPageModule)
  },
  {
    path: 'marketing-content-creation',
    loadChildren: () => import('./pages/marketing-content-creation/marketing-content-creation.module').then(m => m.MarketingContentCreationPageModule)
  },
  {
    path: 'onboarding',
    loadChildren: () => import('./pages/public/onboarding/onboarding.module').then(m => m.OnboardingPageModule)
  },
  {
    path: 'management',
    loadChildren: () => import('./pages/team/management/management.module').then(m => m.ManagementPageModule),
    canActivate: [altomateonlyGuard, authGuard, permissionGuard],
    data: { requiredPermissions: ['Team'] },
  },
  {
    path: 'invitation',
    loadChildren: () => import('./pages/team/invitation/invitation.module').then(m => m.InvitationPageModule)
  },
  {
    path: 'name-check',
    loadChildren: () => import('./pages/name-check/name-check.module').then(m => m.NameCheckPageModule)
  },
  {
    path: 'ai-chat',
    loadChildren: () => import('./pages/ai-chat/ai-chat.module').then(m => m.AiChatPageModule)
  },
  {
    path: 'checkout/:quotationId',
    loadChildren: () => import('./pages/altomate-checkout/altomate-checkout.module').then(m => m.AltomateCheckoutPageModule)
  },
  {
    path: 'in-app-checkout/:quotationId',
    loadChildren: () => import('./pages/altomate-checkout2/altomate-checkout2.module').then(m => m.AltomateCheckout2PageModule)
  },
  {
    path: 'emails-management',
    loadChildren: () => import('./pages/emails-management/emails-management.module').then(m => m.EmailsManagementPageModule)
  },
  // Any address that matches nothing above.
  { path: '**', redirectTo: 'new-not-found' },
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, { onSameUrlNavigation: 'reload', preloadingStrategy: AccountingPreloadingStrategy }),
    OverlayModule,
    PortalModule,
  ],
  exports: [RouterModule]
})
export class AppRoutingModule { }
